import React from 'react';
import { AppNav, View, Text, Badge } from '@smarterservices/smarter-ui';

/**
 * TabItem Component
 * 
 * A reusable tab item component for SessionDetails navigation.
 * Displays a tab with optional notification badge.
 * 
 * @param {Object} props
 * @param {string} props.tabKey - Unique identifier for the tab
 * @param {Object} props.option - Tab configuration object
 * @param {string} props.option.label - Display label for the tab
 * @param {Object} [props.option.notification] - Notification configuration
 * @param {string} [props.option.notification.color] - Badge color variant
 * @param {number} [props.option.notification.count] - Badge count
 * @param {boolean} [props.option.notification.showValue] - Whether to show count value
 * @param {boolean} props.isSelected - Whether this tab is currently selected
 * @param {Function} props.onClick - Click handler for the tab
 */
function TabItem({ tabKey, option, isSelected, onClick }) {
    if (!option) return null;

    const notification = option?.notification;
    const showBadge = notification && notification.count > 0 && (!isSelected || notification.persistWhenSelected);

    return (
        <AppNav.Item
            key={tabKey}
            isSelected={isSelected}
            onClick={onClick}
            renderLabel={
                <View
                    as="span"
                    padding="xx-small small"
                    className={`tab-item ${isSelected ? 'selected' : ''}`}
                    borderWidth={isSelected ? 'none none medium none' : 'none'}
                    borderColor="primary"
                    style={{
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Text
                        size="small"
                        weight={isSelected ? 'bold' : 'normal'}
                        color={'primary-inverse'}
                    >
                        {option.label}
                        {showBadge && (
                            <Badge
                                type={notification.showValue ? "count" : "notification"}
                                standalone={true}
                                variant={notification.color || 'danger'}
                                margin="none none none xx-small"
                                count={notification.count}
                                themeOverride={{
                                    size: '1rem',
                                    fontSize: '.5rem',
                                }}
                            />
                        )}
                    </Text>
                </View>
            }
            notification={notification}
        />
    );
}

export default TabItem;
